import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const SUPPORTED_LANGUAGES = ['pt', 'en', 'fr', 'ar', 'zh'];

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'pt');

  const normalizeLang = (lng) => {
    if (!lng) return null;
    const n = SUPPORTED_LANGUAGES.includes(lng) ? lng : String(lng).split('-')[0];
    return SUPPORTED_LANGUAGES.includes(n) ? n : null;
  };

  // Authoritatively apply the persisted language on mount so EVERY route
  // — including pages without a header/LanguageSwitcher (e.g. /brand-studio)
  // — renders in the user's chosen language.
  useEffect(() => {
    let stored = null;
    try { stored = window.localStorage.getItem('i18nextLng'); } catch (_) { /* no-op */ }
    const target = normalizeLang(stored) || normalizeLang(i18n.language) || 'pt';
    if (i18n.language !== target) {
      i18n.changeLanguage(target);
    }
    document.documentElement.dir = target === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = target;
    setCurrentLanguage(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleLanguageChange = (lng) => {
      // Normalize language code (e.g., 'pt-BR' -> 'pt')
      const final = normalizeLang(lng) || 'pt';
      setCurrentLanguage(final);

      // RTL for Arabic
      document.documentElement.dir = final === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.lang = final;
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => { i18n.off('languageChanged', handleLanguageChange); };
  }, [i18n]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setCurrentLanguage(lng);
  };

  const value = {
    language: currentLanguage,
    changeLanguage,
    isPortuguese: currentLanguage === 'pt',
    isEnglish: currentLanguage === 'en',
    isFrench: currentLanguage === 'fr',
    isArabic: currentLanguage === 'ar',
    isChinese: currentLanguage === 'zh',
    isRTL: currentLanguage === 'ar'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
